import React, { useState, useEffect } from 'react';
import { 
  X, Shield, Users, Calendar, Clock, 
  Check, Globe
} from 'lucide-react';
import { supabase } from '../../config/supabase';
import { KnowledgeFile, PermissionLevel, FilePermission } from '../../types';
import { 
  PERMISSION_LABELS, 
  PERMISSION_COLORS,
  generatePublicAccessToken,
  getPermissionDescription
} from '../../utils/permissions';
import { cn } from '../../utils/cn';

interface FilePermissionModalProps {
  file: KnowledgeFile;
  onClose: () => void;
  onUpdate: () => void;
}

const FilePermissionModal: React.FC<FilePermissionModalProps> = ({
  file,
  onClose,
  onUpdate
}) => {
  const [activeTab, setActiveTab] = useState<'general' | 'users'>('general');
  const [isLoading, setIsLoading] = useState(false);
  const [users, setUsers] = useState<any[]>([]);
  const [filePermissions, setFilePermissions] = useState<FilePermission[]>([]);
  
  // 基本设置
  const [minPermissionLevel, setMinPermissionLevel] = useState<PermissionLevel>(
    file.min_permission_level || 'basic'
  );
  const [isPublic, setIsPublic] = useState(file.is_public || false);
  const [publicExpiresAt, setPublicExpiresAt] = useState(
    file.public_expires_at ? file.public_expires_at.split('T')[0] : ''
  );
  const [ownerOnly, setOwnerOnly] = useState<boolean>(!!file.owner_only);

  const permissionLevels: PermissionLevel[] = ['basic', 'intermediate', 'advanced', 'admin'];

  useEffect(() => {
    fetchUsers();
    fetchFilePermissions();
  }, []);

  const fetchUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('v_fs_users')
        .select('id, email, name, role')
        .order('email');
      
      if (error) throw error;
      setUsers(data || []);
    } catch (error) {
      console.error('获取用户列表失败:', error);
    }
  };

  const fetchFilePermissions = async () => {
    try {
      const { data, error } = await supabase
        .from('file_permissions')
        .select(`
          *,
          users!file_permissions_user_id_fkey(email, name),
          granted_by_user:users!file_permissions_granted_by_fkey(email, name)
        `)
        .eq('file_id', file.id);
      
      if (error) throw error;
      setFilePermissions(data || []);
    } catch (error) {
      console.error('获取文件权限失败:', error);
    }
  };

  const handleSaveGeneral = async () => {
    setIsLoading(true);
    try {
      const updates: any = {
        min_permission_level: minPermissionLevel,
        is_public: isPublic && !ownerOnly,
        owner_only: ownerOnly,
      };

      // 如果设置为公开且没有访问令牌，生成一个
      if (isPublic && !ownerOnly && !file.public_access_token) {
        updates.public_access_token = generatePublicAccessToken();
      }

      // 设置过期时间
      if (!ownerOnly && isPublic && publicExpiresAt) {
        updates.public_expires_at = new Date(publicExpiresAt + 'T23:59:59').toISOString();
      } else {
        updates.public_expires_at = null;
      }

      const { error } = await supabase
        .from('files')
        .update(updates)
        .eq('id', file.id);

      if (error) throw error;

      onUpdate();
      onClose();
    } catch (error) {
      console.error('保存权限设置失败:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGrantPermission = async (userId: string, permissionLevel: PermissionLevel) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('未登录');

      const { error } = await supabase
        .from('file_permissions')
        .upsert({
          file_id: file.id,
          user_id: userId,
          permission_level: permissionLevel,
          granted_by: user.id,
        });

      if (error) throw error;
      
      await fetchFilePermissions();
    } catch (error) {
      console.error('授权失败:', error);
    }
  };

  const handleRevokePermission = async (permissionId: string) => {
    try {
      const { error } = await supabase
        .from('file_permissions')
        .delete()
        .eq('id', permissionId);

      if (error) throw error;
      
      await fetchFilePermissions();
    } catch (error) {
      console.error('撤销权限失败:', error);
    }
  };

  const getPublicUrl = () => {
    if (!file.public_access_token) return '';
    return `${window.location.origin}/portal/${file.public_access_token}`;
  };

  const copyPublicUrl = async () => {
    const url = getPublicUrl();
    if (url) {
      await navigator.clipboard.writeText(url);
      // 这里可以添加一个提示
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={(e) => {
        // 点击背景关闭模态框
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div 
        className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 头部 */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center">
            <Shield className="w-6 h-6 text-teal-600 mr-3" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900">文件权限设置</h2>
              <p className="text-sm text-gray-600">{file.name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* 标签页 */}
        <div className="border-b">
          <nav className="flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('general')}
              className={cn(
                "py-4 px-1 border-b-2 font-medium text-sm",
                activeTab === 'general'
                  ? "border-teal-500 text-teal-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              )}
            >
              基本设置
            </button>
            <button
              onClick={() => setActiveTab('users')}
              className={cn(
                "py-4 px-1 border-b-2 font-medium text-sm",
                activeTab === 'users'
                  ? "border-teal-500 text-teal-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              )}
            >
              用户权限
            </button>
          </nav>
        </div>

        {/* 内容区域 */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {activeTab === 'general' ? (
            <div className="space-y-6">
              {/* 仅自己可见（私密） */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700">
                    私密（仅自己可见）
                  </label>
                  <button
                    onClick={() => {
                      const next = !ownerOnly;
                      setOwnerOnly(next);
                      if (next) {
                        setIsPublic(false);
                      }
                    }}
                    className={cn(
                      "relative inline-flex h-6 w-11 items-center rounded-full transition-colors",
                      ownerOnly ? "bg-teal-600" : "bg-gray-200"
                    )}
                  >
                    <span
                      className={cn(
                        "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                        ownerOnly ? "translate-x-6" : "translate-x-1"
                      )}
                    />
                  </button>
                </div>
                <p className="text-xs text-gray-500">开启后，只有文件创建者可见，且不会生成公开链接。</p>
              </div>
              {/* 最低权限级别 */}
              {!ownerOnly ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    最低访问权限级别
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {permissionLevels.map((level) => (
                      <button
                        key={level}
                        onClick={() => setMinPermissionLevel(level)}
                        className={cn(
                          "p-4 border rounded-lg text-left transition-colors",
                          minPermissionLevel === level
                            ? "border-teal-500 bg-teal-50"
                            : "border-gray-200 hover:border-gray-300"
                        )}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className={cn(
                            "px-2 py-1 rounded text-xs font-medium",
                            PERMISSION_COLORS[level]
                          )}>
                            {PERMISSION_LABELS[level]}
                          </span>
                          {minPermissionLevel === level && (
                            <Check className="w-4 h-4 text-teal-600" />
                          )}
                        </div>
                        <p className="text-sm text-gray-600">
                          {getPermissionDescription(level)}
                        </p>
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    最低访问权限级别
                  </label>
                  <div className="p-4 border border-dashed rounded-lg bg-gray-50 text-gray-500 text-sm">
                    已设置为私密，权限级别设置将被忽略；若取消私密可继续使用当前选择。
                  </div>
                </div>
              )}

              {/* 公开访问 */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-gray-700">
                    公开访问
                  </label>
                  <button
                    disabled={ownerOnly}
                    onClick={() => !ownerOnly && setIsPublic(!isPublic)}
                    className={cn(
                      "relative inline-flex h-6 w-11 items-center rounded-full transition-colors",
                      ownerOnly ? "bg-gray-200 opacity-60 cursor-not-allowed" : isPublic ? "bg-teal-600" : "bg-gray-200"
                    )}
                  >
                    <span
                      className={cn(
                        "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                        isPublic && !ownerOnly ? "translate-x-6" : "translate-x-1"
                      )}
                    />
                  </button>
                </div>
                
                {isPublic && !ownerOnly && (
                  <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center text-sm text-gray-600">
                      <Globe className="w-4 h-4 mr-2" />
                      启用后，任何人都可以通过链接访问此文件
                    </div>
                    
                    {file.public_access_token && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          公开链接
                        </label>
                        <div className="flex">
                          <input
                            type="text"
                            value={getPublicUrl()}
                            readOnly
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md bg-gray-50 text-sm"
                          />
                          <button
                            onClick={copyPublicUrl}
                            className="px-3 py-2 bg-teal-600 text-white rounded-r-md hover:bg-teal-700 text-sm"
                          >
                            复制
                          </button>
                        </div>
                      </div>
                    )}
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        过期时间（可选）
                      </label>
                      <input
                        type="date"
                        value={publicExpiresAt}
                        onChange={(e) => setPublicExpiresAt(e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                        className="px-3 py-2 border border-gray-300 rounded-md focus:ring-teal-500 focus:border-teal-500"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* 用户权限列表 */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">已授权用户</h3>
                {filePermissions.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Users className="w-8 h-8 mx-auto mb-2" />
                    <p>暂无特殊权限用户</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filePermissions.map((permission) => (
                      <div
                        key={permission.id}
                        className="flex items-center justify-between p-4 border border-gray-200 rounded-lg"
                      >
                        <div className="flex items-center">
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">
                              {(permission as any).users?.email}
                            </p>
                            <div className="flex items-center mt-1 text-xs text-gray-500">
                              <Calendar className="w-3 h-3 mr-1" />
                              授权时间: {new Date(permission.granted_at).toLocaleDateString()}
                              {permission.expires_at && (
                                <>
                                  <Clock className="w-3 h-3 ml-3 mr-1" />
                                  过期时间: {new Date(permission.expires_at).toLocaleDateString()}
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-3">
                          <span className={cn(
                            "px-2 py-1 rounded text-xs font-medium",
                            PERMISSION_COLORS[permission.permission_level]
                          )}>
                            {PERMISSION_LABELS[permission.permission_level]}
                          </span>
                          
                          <button
                            onClick={() => handleRevokePermission(permission.id)}
                            className="text-red-600 hover:text-red-800 text-sm"
                          >
                            撤销
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* 添加用户权限 */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">授权新用户</h3>
                <div className="space-y-3">
                  {users
                    .filter(user => !filePermissions.some(p => p.user_id === user.id))
                    .map((user) => (
                      <div
                        key={user.id}
                        className="flex items-center justify-between p-4 border border-gray-200 rounded-lg"
                      >
                        <div>
                          <p className="text-sm font-medium text-gray-900">{user.email}</p>
                          <p className="text-xs text-gray-500">
                            当前角色: {PERMISSION_LABELS[user.role as PermissionLevel]}
                          </p>
                        </div>
                        
                        <div className="flex space-x-2">
                          {permissionLevels.map((level) => (
                            <button
                              key={level}
                              onClick={() => handleGrantPermission(user.id, level)}
                              className={cn(
                                "px-3 py-1 rounded text-xs font-medium transition-colors",
                                PERMISSION_COLORS[level],
                                "hover:opacity-80"
                              )}
                            >
                              {PERMISSION_LABELS[level]}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* 底部按钮 */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            取消
          </button>
          
          {activeTab === 'general' && (
            <button
              onClick={handleSaveGeneral}
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-teal-600 border border-transparent rounded-md hover:bg-teal-700 disabled:opacity-50"
            >
              {isLoading ? '保存中...' : '保存设置'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default FilePermissionModal;
